import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Spacer,
  Text,
  VStack,
} from "@chakra-ui/react";
import { TbBrandPython, TbMathFunction } from "react-icons/tb";
import { HiOutlineDatabase } from "react-icons/hi";
import { MdCode } from "react-icons/md";
import { FaLongArrowAltDown, FaMinus, FaPlus } from "react-icons/fa";
import { DataFrameAnalysisProto, DataFrameAnalysisProto_ParentHeader, PandasDataFrameProto } from "build-ts/dataframe";
import FormattedPandasTable from "./FormattedPandasTable";
import embed from "vega-embed";
import ArtifactPanelHeader from "components/agentChat/ArtifactPanelHeader";
import { FaTableList } from "react-icons/fa6";
import { TiFlowMerge } from "react-icons/ti";
import { DownloadIcon, ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import { FaRegFileExcel } from "react-icons/fa";
import { IoCopyOutline } from "react-icons/io5";
import { copyRawTableToClipboard, downloadRawTableAsExcel, extractRawDataFromPandasDf, getPandasDfRowCount } from "components/analyse/data_table/dataTableUtils";

type TabId = "data" | "charts" | "lineage" | "code";

interface DataFrameAnalysisComponentProps {
  data: DataFrameAnalysisProto;
  onClose?: () => void;
  /** No-op in the panel context — collapse is handled by the artifact strip. */
  collapseSignal?: number;
  onSourceClick?: (ph: DataFrameAnalysisProto_ParentHeader) => void;
  initialActiveTab?: string;
  initialSelectedItemIndex?: number;
  /** Whether the artifact panel is currently expanded to full width */
  isExpanded?: boolean;
  /** Called when the expand/collapse toggle button is clicked */
  onExpandToggle?: () => void;
}

const VALID_TABS: TabId[] = ["data", "charts", "lineage", "code"];

function isValidTabId(tab?: string): tab is TabId {
  return !!tab && VALID_TABS.includes(tab as TabId);
}

const LineageStepLabel: React.FC<{
  icon: React.ElementType;
  children: React.ReactNode;
}> = ({ icon, children }) => (
  <HStack spacing={1.5} mb={3} align="center">
    <Icon as={icon} boxSize={5} color="gray.600" flexShrink={0} aria-hidden />
    <Text
      fontSize="sm"
      fontWeight="700"
      color="gray.700"
      letterSpacing="0.05em"
      textTransform="uppercase"
      mb={0}
    >
      {children}
    </Text>
  </HStack>
);

const LineageFlowConnector: React.FC = () => (
  <Box display="flex" justifyContent="center" w="100%" role="separator" aria-hidden py={3}>
    <Icon as={FaLongArrowAltDown} color="purple.500" boxSize={9} flexShrink={0} />
  </Box>
);

/** One lineage column section: step label + body + optional connector below (between steps). */
const LineageStep: React.FC<{
  labelIcon: React.ElementType;
  label: React.ReactNode;
  showConnectorAfter?: boolean;
  children: React.ReactNode;
}> = ({ labelIcon, label, showConnectorAfter, children }) => (
  <>
    <LineageStepLabel icon={labelIcon}>{label}</LineageStepLabel>
    {children}
    {showConnectorAfter ? <LineageFlowConnector /> : null}
  </>
);

type LineageStepCardProps = {
  title: string;
  subtitle: string;
  icon: React.ElementType;
  onClick?: () => void;
  /** When false, card looks disabled (sources with no catalog handler). */
  interactive?: boolean;
  "aria-label"?: string;
};

/** Shared clickable row for raw source, Python, and results — one visual language. */
const LineageStepCard: React.FC<LineageStepCardProps> = ({
  title,
  subtitle,
  icon,
  onClick,
  interactive = true,
  "aria-label": ariaLabel,
}) => {
  const canInteract = interactive && Boolean(onClick);

  return (
    <Box
      as="button"
      type="button"
      w="100%"
      textAlign="left"
      px={4}
      py={3}
      borderRadius="md"
      border="1.5px solid"
      borderColor="gray.400"
      bg="white"
      boxShadow="sm"
      transition="all 0.15s"
      cursor={canInteract ? "pointer" : "not-allowed"}
      opacity={canInteract ? 1 : 0.65}
      onClick={onClick}
      aria-label={ariaLabel}
      _hover={
        canInteract ? { borderColor: "purple.400", bg: "purple.50", boxShadow: "md" } : undefined
      }
      _active={canInteract ? { transform: "scale(0.99)" } : undefined}
    >
      <HStack align="flex-start" spacing={2.5}>
        <Icon as={icon} boxSize={5} color="purple.600" flexShrink={0} mt={0.5} aria-hidden />
        <VStack align="start" spacing={0.5} flex="1" minW={0}>
          <Text fontSize="sm" fontWeight="700" color="gray.800" mb={0} lineHeight="1.2" isTruncated w="100%">
            {title}
          </Text>
          <Text fontSize="xs" color="gray.700" mb={0} lineHeight="1.3">
            {subtitle}
          </Text>
        </VStack>
      </HStack>
    </Box>
  );
};

const LineageEmptyPlaceholder: React.FC<{
  message: string;
  /** dashed border (sources); solid lighter border (published empty state). */
  dashed?: boolean;
  muted?: boolean;
}> = ({ message, dashed = true, muted = false }) => (
  <Box
    w="100%"
    px={3}
    py={2.5}
    borderRadius="md"
    borderWidth={dashed ? "1.5px" : "1px"}
    borderStyle={dashed ? "dashed" : "solid"}
    borderColor={muted ? "gray.200" : "gray.300"}
    bg="gray.50"
  >
    <Text fontSize="2xs" color={muted ? "gray.500" : "gray.400"} mb={0} textAlign="center">
      {message}
    </Text>
  </Box>
);

// ── Lineage tab (vertical: raw → code → published) ──────────────────────────
const LineageTab: React.FC<{
  data: DataFrameAnalysisProto;
  onSourceClick?: (ph: DataFrameAnalysisProto_ParentHeader) => void;
  onOpenCodeTab: () => void;
  onSelectPublishedFrame: (index: number) => void;
}> = ({ data, onSourceClick, onOpenCodeTab, onSelectPublishedFrame }) => {
  const publishedFrames = data.pandas_dataframes ?? [];
  const publishedCount = publishedFrames.length;
  const parents = data.parent_headers ?? [];
  const codeStr = data.code ?? "";
  const collapsibleCodeBlockCount = useMemo(
    () => getCollapsiblePythonCodeSectionIndices(codeStr).length,
    [codeStr],
  );
  const pythonStepTitle =
    !codeStr.trim()
      ? "No code snippet"
      : collapsibleCodeBlockCount > 0
        ? `Wrote ${collapsibleCodeBlockCount} code ${collapsibleCodeBlockCount === 1 ? "block" : "blocks"}`
        : "Wrote code snippet";

  return (
    <VStack align="stretch" spacing={0} px={3} py={8} maxW="20rem" mx="auto" w="100%">
      <LineageStep labelIcon={HiOutlineDatabase} label="Data Query" showConnectorAfter>
        {parents.length > 0 ? (
          <VStack align="stretch" spacing={2} w="100%">
            {parents.map((ph: DataFrameAnalysisProto_ParentHeader, i: number) => (
              <LineageStepCard
                key={ph.name ?? i}
                icon={FaTableList}
                title={ph.name ?? `Source ${i + 1}`}
                subtitle="View table"
                interactive={Boolean(onSourceClick)}
                onClick={() => onSourceClick?.(ph)}
              />
            ))}
          </VStack>
        ) : (
          <LineageEmptyPlaceholder message="No source tables" />
        )}
      </LineageStep>

      <LineageStep labelIcon={TbMathFunction} label="Python" showConnectorAfter>
        <LineageStepCard
          icon={TbBrandPython}
          title={pythonStepTitle}
          subtitle="View script"
          onClick={onOpenCodeTab}
          aria-label="View Python code in the Code tab"
        />
      </LineageStep>

      <LineageStep labelIcon={FaTableList} label="Results">
        {publishedCount > 0 ? (
          <VStack align="stretch" spacing={2} w="100%">
            {publishedFrames.map((df: PandasDataFrameProto, i: number) => (
              <LineageStepCard
                key={df.name ?? i}
                icon={FaTableList}
                title={df.name ?? `Result ${i + 1}`}
                subtitle="Open in Results tab"
                onClick={() => onSelectPublishedFrame(i)}
              />
            ))}
          </VStack>
        ) : (
          <LineageEmptyPlaceholder message="No published dataframes" dashed={false} muted />
        )}
      </LineageStep>
    </VStack>
  );
};

/** True when the line is a Python full-line # comment (optional indent, then #). */
const isHashCommentLine = (line: string) => /^\s*#/.test(line);

/** Display-only: hide leading `#` / `##` and spacing so comments read as prose (parsing still uses raw lines). */
function stripHashCommentDisplay(line: string): string {
  if (!isHashCommentLine(line)) return line;
  return line.replace(/^\s*#+\s*/, "");
}

/**
 * Split on full-line # blocks: each section = consecutive # lines + following code until the next # block.
 * Code before any # is one section with empty `comment`.
 */
function splitPythonIntoCommentLedSections(source: string): { comment: string; code: string }[] {
  const lines = source.split("\n");
  const out: { comment: string; code: string }[] = [];
  let cbuf: string[] = [];
  let xbuf: string[] = [];
  const hasCode = () => xbuf.join("\n").trim().length > 0;

  for (const line of lines) {
    if (/^\s*#/.test(line)) {
      if (hasCode()) {
        out.push({ comment: cbuf.join("\n"), code: xbuf.join("\n") });
        cbuf = [line];
        xbuf = [];
      } else {
        cbuf.push(line);
      }
    } else {
      xbuf.push(line);
    }
  }
  if (cbuf.join("\n").trim().length > 0 || hasCode()) {
    out.push({ comment: cbuf.join("\n"), code: xbuf.join("\n") });
  }
  return out;
}

/** Lines of code with real content — ignores blank lines and does not treat bare newlines as extra lines. */
const countCodeLines = (raw: string): number => {
  if (!raw.trim()) return 0;
  return raw.split(/\r?\n/).filter((line) => line.trim().length > 0).length;
};

/** Leading section: code before the first full-line `#` — shown inline, not in expand/collapse list. */
function isPythonPrologueSection(sec: { comment: string; code: string }, i: number): boolean {
  return i === 0 && !sec.comment.trim() && countCodeLines(sec.code) > 0;
}

/**
 * Section indices that get per-block expand/collapse in the Code tab — same rules as `indexWithCode`
 * there: has code lines, and not the leading prologue block.
 */
function getCollapsiblePythonCodeSectionIndices(source: string): number[] {
  if (!source.trim()) return [];
  const sections = splitPythonIntoCommentLedSections(source);

  return sections
    .map((s, i) => {
      if (countCodeLines(s.code) <= 0) return null;
      if (isPythonPrologueSection(s, i)) return null;
      return i;
    })
    .filter((x): x is number => x !== null);
}

/** Matches IconButton column width for aligning prologue + expanded snippets. */
const CODE_TAB_GUTTER_SLOT_W = 8;

/** Split source into lines without duplicating terminal newline as an extra blank row (split gives a trailing ""). */
function splitIntoDisplayLines(source: string): string[] {
  const normalized = source.replace(/\r/g, "");
  let lines = normalized.split("\n");
  if (lines.length > 1 && lines[lines.length - 1] === "") {
    lines = lines.slice(0, -1);
  }
  return lines;
}

const CodeSnippetBody: React.FC<{ source: string }> = ({ source }) => (
  <Box as="code" display="block" color="gray.800" fontSize="inherit" fontFamily="inherit">
    {splitIntoDisplayLines(source).map((line, i) => {
      const bold = isHashCommentLine(line);
      const shown = stripHashCommentDisplay(line);
      return (
        <Text
          key={i}
          as="span"
          display="block"
          fontWeight={bold ? "700" : "500"}
          color="inherit"
          mb={0}
          whiteSpace="pre-wrap"
          lineHeight="2"
        >
          {shown.length === 0 ? "\u00a0" : shown}
        </Text>
      );
    })}
  </Box>
);

// ── Code tab ───────────────────────────────────────────────────────────────
const CodeTab: React.FC<{ code?: string }> = ({ code }) => {
  const sections = useMemo(() => (code?.trim() ? splitPythonIntoCommentLedSections(code) : []), [code]);

  /** Indices that use collapse (excludes leading code with no comment). Must run before any early return. */
  const indexWithCode = useMemo(() => getCollapsiblePythonCodeSectionIndices(code ?? ""), [code]);

  /** Per-section expanded when that section has collapsible code; default collapsed. */
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});

  if (!code?.trim()) {
    return (
      <Box px={4} py={6} textAlign="center">
        <Text fontSize="sm" color="gray.400" fontStyle="italic" mb={0}>
          No code available.
        </Text>
      </Box>
    );
  }

  const collapseAll = () => setExpanded({});

  const expandAll = () => {
    const next: Record<number, boolean> = {};
    indexWithCode.forEach((idx) => {
      next[idx] = true;
    });
    setExpanded(next);
  };

  const allCollapsibleExpanded =
    indexWithCode.length > 0 && indexWithCode.every((idx) => expanded[idx]);

  const toggleExpandAll = () => {
    if (allCollapsibleExpanded) collapseAll();
    else expandAll();
  };

  const toggleSection = (i: number) => setExpanded((prev) => ({ ...prev, [i]: !prev[i] }));

  const hasAnyCollapsible = indexWithCode.length > 0;

  return (
    <Box
      m={0}
      w="100%"
      px={2}
      pt={0}
      pb={2}
      overflowX="auto"
      fontSize="xs"
      lineHeight="1.65"
      textAlign="left"
      bg="white"
      sx={{
        "&::-webkit-scrollbar": { width: "4px", height: "4px" },
        "&::-webkit-scrollbar-track": { background: "transparent" },
        "&::-webkit-scrollbar-thumb": { background: "var(--chakra-colors-gray-300)", borderRadius: "2px" },
      }}
    >
      {/* Sticky within the artifact tab scroll area — does not scroll away with snippets */}
      {hasAnyCollapsible && (
        <Flex
          justify="flex-end"
          position="sticky"
          top={0}
          zIndex={10}
          bg="transparent"
          py={1}
          mb={0}
        >
          <Button
            size="xs"
            variant="outline"
            colorScheme="purple"
            bg="white"
            onClick={toggleExpandAll}
            leftIcon={allCollapsibleExpanded ? <ViewOffIcon boxSize={3.5} /> : <ViewIcon boxSize={3.5} />}
          >
            {allCollapsibleExpanded ? "Collapse code blocks" : "Expand code blocks"}
          </Button>
        </Flex>
      )}

      <VStack align="stretch" spacing={2} pt={0} divider={<Divider borderColor="gray.100" my={0} />} fontFamily="mono">
        {sections.map((sec, i) => {
          const commentLines = sec.comment.length ? sec.comment.split("\n") : [];
          const lineCount = countCodeLines(sec.code);
          const hasCodeBlock = lineCount > 0;
          const isOpen = expanded[i] ?? false;

          if (!hasCodeBlock) {
            return (
              <Box key={i}>
                {commentLines.length > 0 ? (
                  <Box>
                    {commentLines.map((line, li) => {
                      const bold = isHashCommentLine(line);
                      const shown = stripHashCommentDisplay(line);
                      return (
                        <Text
                          key={li}
                          as="span"
                          display="block"
                          fontWeight={bold ? "700" : "400"}
                          color="gray.800"
                          mb={0}
                          whiteSpace="pre-wrap"
                        >
                          {shown.length === 0 ? "\u00a0" : shown}
                        </Text>
                      );
                    })}
                  </Box>
                ) : null}
              </Box>
            );
          }

          /** Leading code before the first `#` — always visible, no toggle. */
          if (isPythonPrologueSection(sec, i)) {
            return (
              <Box key={i}>
                <HStack align="flex-start" spacing={2}>
                  <Box flexShrink={0} w={CODE_TAB_GUTTER_SLOT_W} aria-hidden />
                  <Box flex="1" minW={0}>
                    <Box
                      as="pre"
                      m={0}
                      p={0}
                      overflowX="auto"
                      fontFamily="mono"
                      fontSize="inherit"
                      sx={{
                        "&::-webkit-scrollbar": { height: "4px" },
                        "&::-webkit-scrollbar-thumb": {
                          background: "var(--chakra-colors-gray-300)",
                          borderRadius: "2px",
                        },
                      }}
                    >
                      <CodeSnippetBody source={sec.code} />
                    </Box>
                  </Box>
                </HStack>
              </Box>
            );
          }

          return (
            <Box key={i}>
              <HStack align="flex-start" spacing={2} mb={isOpen ? 0 : 1}>
                <Box flexShrink={0} w={CODE_TAB_GUTTER_SLOT_W} display="flex" justifyContent="center" pt={0.5}>
                  <IconButton
                    size="2xs"
                    variant="ghost"
                    bg="purple.50"
                    color="purple.700"
                    _hover={{ bg: "purple.100", color: "purple.700" }}
                    _active={{ bg: "purple.200" }}
                    aria-label={isOpen ? "Hide code under this comment" : "Show code under this comment"}
                    aria-expanded={isOpen}
                    icon={<Icon as={isOpen ? FaMinus : FaPlus} boxSize={3} color="purple.700" />}
                    onClick={() => toggleSection(i)}
                  />
                </Box>
                <Box flex="1" minW={0}>
                  {commentLines.length > 0 ? (
                    <Box mb={0}>
                      {commentLines.map((line, li) => {
                        const bold = isHashCommentLine(line);
                        const shown = stripHashCommentDisplay(line);
                        return (
                          <Text
                            key={li}
                            as="span"
                            display="block"
                            fontWeight={bold ? "600" : "400"}
                            color="gray.800"
                            mb={0}
                            whiteSpace="pre-wrap"
                            fontSize="0.8rem"
                          >
                            {shown.length === 0 ? "\u00a0" : shown}
                          </Text>
                        );
                      })}
                    </Box>
                  ) : null}
                  {!isOpen ? (
                    <Text fontSize="xs" color="gray.600" mb={0} mt={commentLines.length > 0 ? 1 : 0}>
                      ({lineCount} line{lineCount === 1 ? "" : "s"})
                    </Text>
                  ) : null}
                </Box>
              </HStack>
              <HStack align="flex-start" spacing={2} mt={0} pt={0}>
                <Box flexShrink={0} w={CODE_TAB_GUTTER_SLOT_W} aria-hidden />
                <Box flex="1" minW={0} pt={0}>
                  {/* Plain show/hide: Chakra Collapse height animation breaks in nested flex when many sections open at once */}
                  {isOpen ? (
                    <Box
                      as="pre"
                      m={0}
                      p={0}
                      overflowX="auto"
                      fontFamily="mono"
                      fontSize="inherit"
                      sx={{
                        "&::-webkit-scrollbar": { height: "4px" },
                        "&::-webkit-scrollbar-thumb": {
                          background: "var(--chakra-colors-gray-300)",
                          borderRadius: "2px",
                        },
                      }}
                    >
                      <CodeSnippetBody source={sec.code} />
                    </Box>
                  ) : null}
                </Box>
              </HStack>
            </Box>
          );
        })}
      </VStack>
    </Box>
  );
};

const AnalysisChartTab: React.FC<{ chartVegaSpecJson?: string }> = ({ chartVegaSpecJson }) => {
  const chartContainerRef = React.useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let finalized: (() => void) | undefined;
    const render = async () => {
      if (!chartContainerRef.current || !chartVegaSpecJson) return;
      try {
        const parsed = JSON.parse(chartVegaSpecJson) as Record<string, unknown>;
        const result = await embed(chartContainerRef.current, parsed, {
          actions: false,
          renderer: "svg",
        });
        finalized = result.finalize;
      } catch {
        // Keep chart rendering resilient if payload is malformed.
      }
    };
    render();
    return () => {
      if (finalized) finalized();
    };
  }, [chartVegaSpecJson]);

  if (!chartVegaSpecJson) {
    return (
      <Box px={4} py={6} textAlign="center">
        <Text fontSize="sm" color="gray.400" fontStyle="italic" mb={0}>
          No chart available.
        </Text>
      </Box>
    );
  }
  return (
    <Box px={2} py={2} textAlign="center">
      <Box ref={chartContainerRef} w="100%" />
    </Box>
  );
};

// ── Main component ─────────────────────────────────────────────────────────
const DataFrameAnalysisComponent: React.FC<DataFrameAnalysisComponentProps> = ({
  data,
  onClose,
  collapseSignal,
  onSourceClick,
  initialActiveTab,
  initialSelectedItemIndex,
  isExpanded,
  onExpandToggle,
}) => {
  const [activeTab, setActiveTab] = useState<TabId>(isValidTabId(initialActiveTab) ? initialActiveTab : "data");
  const [selectedDfIndex, setSelectedDfIndex] = useState(
    isValidTabId(initialActiveTab) && initialActiveTab === "data"
      ? Math.max(initialSelectedItemIndex ?? 0, 0)
      : 0
  );
  const [selectedChartIndex, setSelectedChartIndex] = useState(initialSelectedItemIndex ?? 0);
  useEffect(() => {
    if (isValidTabId(initialActiveTab)) {
      setActiveTab(initialActiveTab);
    }
  }, [initialActiveTab]);

  useEffect(() => {
    if (initialSelectedItemIndex !== undefined) {
      const nextIndex = Math.max(initialSelectedItemIndex, 0);
      if (initialActiveTab === "data") {
        setSelectedDfIndex(nextIndex);
      } else if (initialActiveTab === "charts") {
        setSelectedChartIndex(nextIndex);
      }
    }
  }, [initialActiveTab, initialSelectedItemIndex]);


  const dataframes = data.pandas_dataframes ?? [];
  const activeDf = dataframes[selectedDfIndex] ?? null;

  // Derived directly from activeDf to avoid parent/child useEffect ordering issues.
  const numberOfRows = useMemo(() => getPandasDfRowCount(activeDf ?? undefined), [activeDf]);
  const charts = (
    data as unknown as { charts?: Array<{ name?: string; spec_json?: string }> }
  ).charts ?? [];
  const activeChart = charts[selectedChartIndex] ?? null;

  useEffect(() => {
    if (selectedChartIndex >= charts.length) {
      setSelectedChartIndex(0);
    }
  }, [charts.length, selectedChartIndex]);

  useEffect(() => {
    if (selectedDfIndex >= dataframes.length) {
      setSelectedDfIndex(0);
    }
  }, [dataframes.length, selectedDfIndex]);

  const tabs: { id: TabId; label: string; icon: React.ElementType }[] = [
    { id: "data", label: "Data", icon: FaTableList },
    ...(charts.length > 0 ? [{ id: "charts" as TabId, label: "Charts", icon: HiOutlineDatabase }] : []),
    { id: "lineage", label: "Lineage", icon: TiFlowMerge },
    { id: "code", label: "Code", icon: MdCode },
  ];

  return (
    <Box
      position="relative"
      display="flex"
      flexDirection="column"
      width="100%"
      flex="1 1 0"
      minH={0}
      overflow="hidden"
    >
      <ArtifactPanelHeader
        icon={TbMathFunction}
        iconBg="white"
        iconColor="purple.700"
        name={data.header?.name ?? "Analysis"}
        typeLabel="Analysis"
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={(id) => setActiveTab(id as TabId)}
        isExpanded={isExpanded}
        onExpandToggle={onExpandToggle}
      />

      {/* ── Tab content only: scrolls; header (title + Results|Trace|Code) is fixed above */}
      <Box
        flex="1 1 0"
        minH={0}
        display="flex"
        flexDirection="column"
        overflowY="auto"
        overflowX="hidden"
        onWheel={(e) => e.stopPropagation()}
        sx={{
          "&::-webkit-scrollbar": { width: "4px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": { background: "var(--chakra-colors-gray-300)", borderRadius: "2px" },
        }}
      >
        {/* ── Data Tab ── */}
        {activeTab === "data" && (
          <>
            {/* Dataframe selector (sticky: table scrolls underneath) */}
            <HStack
              spacing={1.5}
              px={2}
              py={1.5}
              borderBottom="1px solid"
              borderColor="gray.100"
              bg="white"
              flexWrap="wrap"
              position="sticky"
              top={0}
              zIndex={2}
              boxShadow="sm"
            >
              {dataframes.map((df: PandasDataFrameProto, i: number) => {
                const isActive = i === selectedDfIndex;
                return (
                  <Box
                    key={df.name ?? i}
                    as="button"
                    type="button"
                    display="inline-flex"
                    alignItems="center"
                    px={3}
                    py={1}
                    borderRadius="md"
                    border={isActive ? "2px solid" : "1px solid"}
                    borderColor={isActive ? "purple.600" : "gray.300"}
                    bg={isActive ? "white" : "white"}
                    color={isActive ? "purple.700" : "gray.600"}
                    fontSize="xs"
                    fontWeight={isActive ? "700" : "600"}
                    _hover={{
                      borderColor: "purple.400",
                      color: isActive ? "purple.700" : "purple.600",
                      bg: "purple.50",
                    }}
                    transition="all 0.15s"
                    cursor="pointer"
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      setSelectedDfIndex(i);
                    }}
                  >
                    <HStack as="span" spacing={1.5} align="center">
                      <Icon
                        as={FaTableList}
                        boxSize={3}
                        color="currentColor"
                        flexShrink={0}
                        aria-hidden
                      />
                      <Text
                        as="span"
                        fontSize="xs"
                        lineHeight="1.2"
                        mb={0}
                        isTruncated
                      >
                        {df.name ?? `Frame ${i + 1}`}
                      </Text>
                    </HStack>
                  </Box>
                );
              })}
              <Spacer />
              {numberOfRows > 0 && (
                <Text fontSize="xs" color="gray.500" mb={0} flexShrink={0}>
                  {numberOfRows} {numberOfRows === 1 ? "row" : "rows"}
                </Text>
              )}
              {activeDf && (
                <Menu placement="bottom-end" gutter={2} closeOnSelect={true}>
                  <MenuButton
                    as={IconButton}
                    size="xs"
                    color="gray.600"
                    bg="transparent"
                    _hover={{ bg: "gray.50", color: "gray.800" }}
                    aria-label="Download"
                    icon={<Icon as={DownloadIcon} boxSize={4} />}
                  />
                  <Portal>
                    <MenuList
                      minW="120px"
                      zIndex={1000}
                      shadow="lg"
                      border="1px solid"
                      borderColor="gray.200"
                      p={1}
                    >
                      <MenuItem
                        icon={<Icon as={IoCopyOutline} boxSize={4} />}
                        onClick={() => copyRawTableToClipboard(extractRawDataFromPandasDf(activeDf))}
                        fontSize="sm"
                      >
                        Copy to clipboard
                      </MenuItem>
                      <MenuItem
                        icon={<Icon as={FaRegFileExcel} boxSize={4} />}
                        onClick={() => downloadRawTableAsExcel(extractRawDataFromPandasDf(activeDf), `${activeDf.name ?? "dataframe"}.xlsx`)}
                        fontSize="sm"
                      >
                        Download Excel file
                      </MenuItem>
                    </MenuList>
                  </Portal>
                </Menu>
              )}
            </HStack>

            {/* Table */}
            {activeDf ? (
              <FormattedPandasTable df={activeDf} />
            ) : (
              <Box px={4} py={6} textAlign="center">
                <Text fontSize="sm" color="gray.400" fontStyle="italic" mb={0}>
                  No dataframes available.
                </Text>
              </Box>
            )}
          </>
        )}

        {/* ── Charts Tab ── */}
        {
          activeTab === "charts" && (
            <VStack align="stretch" spacing={0}>
              {charts.length === 0 ? (
                <Box px={4} py={6} textAlign="center">
                  <Text fontSize="sm" color="gray.400" fontStyle="italic" mb={0}>
                    No charts available.
                  </Text>
                </Box>
              ) : (
                <>
                  {charts.length > 1 && (
                    <HStack
                      spacing={1.5}
                      px={2}
                      py={1.5}
                      borderBottom="1px solid"
                      borderColor="gray.100"
                      bg="white"
                      flexWrap="wrap"
                    >
                      {charts.map((chart: { name?: string }, i: number) => {
                        const isActive = i === selectedChartIndex;
                        return (
                          <Box
                            key={chart.name ?? i}
                            as="button"
                            px={3}
                            py={1}
                            borderRadius="full"
                            border="1.5px solid"
                            borderColor={isActive ? "purple.500" : "gray.300"}
                            bg={isActive ? "purple.500" : "white"}
                            color={isActive ? "white" : "gray.600"}
                            fontSize="xs"
                            fontWeight="600"
                            _hover={{
                              borderColor: "purple.400",
                              color: isActive ? "white" : "purple.600",
                              bg: isActive ? "purple.500" : "purple.50",
                            }}
                            transition="all 0.15s"
                            cursor="pointer"
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              setSelectedChartIndex(i);
                            }}
                          >
                            {chart.name ?? `Chart ${i + 1}`}
                          </Box>
                        );
                      })}
                    </HStack>
                  )}
                  <AnalysisChartTab chartVegaSpecJson={activeChart?.spec_json} />
                </>
              )}
            </VStack>
          )
        }
        {/* ── Lineage Tab ── */}
        {activeTab === "lineage" && (
          <LineageTab
            data={data}
            onSourceClick={onSourceClick}
            onOpenCodeTab={() => setActiveTab("code")}
            onSelectPublishedFrame={(i) => {
              setSelectedDfIndex(i);
              setActiveTab("data");
            }}
          />
        )}

        {/* ── Code Tab */}
        {activeTab === "code" && <CodeTab code={data.code} />}
      </Box >
    </Box >
  );
};

export default DataFrameAnalysisComponent;
